import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Save, Upload, Image as ImageIcon, KeyRound, Search, RefreshCcw, Sun, Moon, Palette, Check, Settings as SettingsIcon, ShieldAlert, Trash2, Receipt, CalendarClock } from 'lucide-react';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import Logo from '@/components/public/Logo';
import { PALETTES, useAdminTheme } from '@/lib/theme';
import ConfirmDialog from '@/components/admin/ConfirmDialog';
import AdminTabs from '@/components/admin/AdminTabs';
import ImagePicker from '@/components/admin/ImagePicker';
import { logger } from '@/lib/logger';

const SIZE_OPTIONS = [
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'Extra Large' },
];

const RADIUS_OPTIONS = [
  { value: 'none', label: 'None (Square)' },
  { value: 'sm', label: 'Slight' },
  { value: 'md', label: 'Rounded' },
  { value: 'lg', label: 'More Rounded' },
  { value: 'full', label: 'Fully Round' },
];

const TABS = [
  { key: 'appearance', label: 'Appearance', icon: Palette, testid: 'admin-settings-tab-appearance' },
  { key: 'brand', label: 'Brand & Logo', icon: ImageIcon, testid: 'admin-settings-tab-brand' },
  { key: 'seo', label: 'SEO', icon: Search, testid: 'admin-settings-tab-seo' },
  { key: 'password', label: 'Password', icon: KeyRound, testid: 'admin-settings-tab-password' },
  { key: 'danger', label: 'Danger Zone', icon: ShieldAlert, testid: 'admin-settings-tab-danger' },
];

export default function AdminSettings() {
  const [tab, setTab] = useState('appearance');

  return (
    <div>
      <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight inline-flex items-center gap-2"><SettingsIcon className="w-7 h-7 text-[var(--pg-admin-accent)]" /> Settings</h1>
      <p className="mt-1 text-sm text-white/60">Manage appearance, brand identity, SEO metadata, admin credentials, and data cleanup.</p>

      <div className="mt-6 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto">
        <AdminTabs
          options={TABS.map((t) => ({ id: t.key, label: t.label, icon: t.icon, testid: t.testid }))}
          value={tab}
          onChange={setTab}
          variant={tab === 'danger' ? 'danger' : 'accent'}
          size="md"
        />
      </div>

      <div className="mt-6">
        {tab === 'appearance' && <AppearanceTab />}
        {tab === 'brand' && <BrandTab />}
        {tab === 'seo' && <SeoTab />}
        {tab === 'password' && <PasswordTab />}
        {tab === 'danger' && <DangerZoneTab />}
      </div>
    </div>
  );
}

const DangerZoneTab = () => {
  const [busy, setBusy] = useState(null); // 'sessions' | 'invoices' | null
  const [confirmSessions, setConfirmSessions] = useState(false);
  const [confirmInvoices, setConfirmInvoices] = useState(false);

  const purgeSessions = async () => {
    setBusy('sessions');
    try {
      const { data } = await api.delete('/admin/sessions');
      toast.success(`Deleted ${data.sessions_deleted} sessions. ${data.invoices_voided} invoices voided.`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete sessions'); }
    finally { setBusy(null); setConfirmSessions(false); }
  };

  const purgeInvoices = async () => {
    setBusy('invoices');
    try {
      const { data } = await api.delete('/admin/invoices');
      toast.success(`Deleted ${data.invoices_deleted} invoices.`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete invoices'); }
    finally { setBusy(null); setConfirmInvoices(false); }
  };

  return (
    <div className="max-w-2xl rounded-2xl border border-red-500/30 bg-[#141414] p-6">
      <h2 className="font-display text-2xl inline-flex items-center gap-2 text-red-300"><ShieldAlert className="w-5 h-5" /> Danger Zone</h2>
      <p className="mt-1 text-sm text-white/60">These actions are permanent and cannot be undone. Use with care.</p>

      <div className="mt-6 space-y-3">
        <div className="rounded-xl border border-white/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="inline-flex w-10 h-10 shrink-0 items-center justify-center rounded-xl bg-red-500/15 text-red-300"><CalendarClock className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold">Delete all sessions</div>
            <div className="text-xs text-white/60">Removes every scheduled/past session. Any linked invoices are voided (kept for audit).</div>
          </div>
          <button
            data-testid="admin-danger-delete-sessions"
            onClick={() => setConfirmSessions(true)}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/15 hover:bg-red-500 hover:text-white text-red-300 border border-red-500/30 transition-colors text-sm font-semibold disabled:opacity-60 shrink-0"
          >
            <Trash2 className="w-4 h-4" /> Delete all sessions
          </button>
        </div>

        <div className="rounded-xl border border-white/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="inline-flex w-10 h-10 shrink-0 items-center justify-center rounded-xl bg-red-500/15 text-red-300"><Receipt className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold">Delete all invoices</div>
            <div className="text-xs text-white/60">Removes every generated invoice permanently. Sessions are kept.</div>
          </div>
          <button
            data-testid="admin-danger-delete-invoices"
            onClick={() => setConfirmInvoices(true)}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/15 hover:bg-red-500 hover:text-white text-red-300 border border-red-500/30 transition-colors text-sm font-semibold disabled:opacity-60 shrink-0"
          >
            <Trash2 className="w-4 h-4" /> Delete all invoices
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmSessions}
        onOpenChange={setConfirmSessions}
        title="Delete ALL sessions?"
        description="This will permanently remove every session and void every related invoice. This action cannot be undone."
        confirmText={busy === 'sessions' ? 'Deleting...' : 'Yes, delete everything'}
        destructive
        onConfirm={purgeSessions}
        confirmTestId="admin-danger-confirm-sessions"
      />
      <ConfirmDialog
        open={confirmInvoices}
        onOpenChange={setConfirmInvoices}
        title="Delete ALL invoices?"
        description="This will permanently remove every invoice. Sessions and their statuses are unaffected."
        confirmText={busy === 'invoices' ? 'Deleting...' : 'Yes, delete everything'}
        destructive
        onConfirm={purgeInvoices}
        confirmTestId="admin-danger-confirm-invoices"
      />
    </div>
  );
};

const AppearanceTab = () => {
  const { theme, setMode, setPalette } = useAdminTheme();

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 rounded-2xl border border-white/10 bg-[#141414] p-6">
        <h2 className="font-display text-2xl inline-flex items-center gap-2"><Palette className="w-5 h-5 text-[var(--pg-admin-accent)]" /> Appearance</h2>
        <p className="mt-1 text-sm text-white/60">Choose a mode and an accent palette for your admin console. Saved locally to this browser.</p>

        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">Mode</div>
          <div className="mt-2 inline-flex rounded-xl border border-white/10 bg-black/30 p-1">
            <button
              data-testid={ADMIN.themeModeLight}
              onClick={() => setMode('light')}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${theme.mode === 'light' ? 'bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)] font-semibold' : 'text-white/70 hover:text-white'}`}
            >
              <Sun className="w-4 h-4" /> Light
            </button>
            <button
              data-testid={ADMIN.themeModeDark}
              onClick={() => setMode('dark')}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${theme.mode === 'dark' ? 'bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)] font-semibold' : 'text-white/70 hover:text-white'}`}
            >
              <Moon className="w-4 h-4" /> Dark
            </button>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">Accent palette</div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {PALETTES.map((p) => {
              const active = theme.palette === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={ADMIN.themePaletteOption(p.id)}
                  onClick={() => setPalette(p.id)}
                  className={`relative rounded-2xl border p-3 text-left transition-all ${active ? 'border-white/40 shadow-[0_0_0_2px_var(--pg-admin-accent-ring)]' : 'border-white/10 hover:border-white/25'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="w-8 h-8 rounded-lg shrink-0" style={{ background: p.swatch }} />
                    {active && <span className="inline-flex w-5 h-5 rounded-full items-center justify-center text-[var(--pg-admin-accent-fg)]" style={{ background: p.accent }}><Check className="w-3 h-3" /></span>}
                  </div>
                  <div className="mt-2 text-sm font-semibold">{p.label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">{p.id}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="lg:col-span-4 rounded-2xl border border-white/10 bg-[#141414] p-6">
        <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">Live preview</div>
        <div className="mt-3 rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
          <button className="w-full inline-flex items-center justify-center gap-2 bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] text-[var(--pg-admin-accent-fg)] font-semibold px-4 py-2 rounded-xl">Primary button</button>
          <div className="rounded-lg border border-[var(--pg-admin-accent-ring)] bg-[var(--pg-admin-accent-soft)] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[var(--pg-admin-accent)] font-semibold">Confirmed session</div>
            <div className="mt-1 text-sm">Group Clinic · 3 clients</div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--pg-admin-accent)' }} />
            Accent dot indicator
          </div>
        </div>
        <p className="mt-3 text-[11px] text-white/50">Changes save instantly to this browser. Log in from another device to set it there.</p>
      </div>
    </div>
  );
};

const BrandTab = () => {
  const [brand, setBrand] = useState({ logo_data_url: '/padel-gems-logo.png', logo_size: 'md', logo_radius: 'md', show_wordmark: false });
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/content');
      const extra = data?.brand?.extra || {};
      setBrand({
        logo_data_url: extra.logo_data_url || '/padel-gems-logo.png',
        logo_size: extra.logo_size || 'md',
        logo_radius: extra.logo_radius || 'md',
        show_wordmark: !!extra.show_wordmark,
      });
    } catch (err) { logger.warn("Silent failure:", err); }
  };
  useEffect(() => { load(); }, []);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g|gif|svg\+xml|webp)$/.test(f.type)) {
      toast.error('Please choose a PNG, JPG, SVG, GIF, or WebP image.'); return;
    }
    if (f.size > 800 * 1024) {
      toast.error('Image is too large. Please keep it under 800 KB.'); return;
    }
    const reader = new FileReader();
    reader.onload = () => setBrand((b) => ({ ...b, logo_data_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/brand', brand);
      toast.success('Brand updated. Reload the site to see the changes everywhere.');
      window.dispatchEvent(new Event('pg-refresh-brand'));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const reset = () => setConfirmReset(true);
  const performReset = () => { setBrand((b) => ({ ...b, logo_data_url: '/padel-gems-logo.png' })); setConfirmReset(false); };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-7 rounded-2xl border border-white/10 bg-[#141414] p-6">
        <h2 className="font-display text-2xl">Logo</h2>
        <p className="mt-1 text-sm text-white/60">Upload a new logo and adjust its display size and corner rounding.</p>

        <div className="mt-6 space-y-5">
          <Field label="Upload new logo (PNG, JPG, SVG, GIF, or WebP, max 800 KB)">
            <div className="flex flex-wrap gap-2">
              <input ref={fileRef} data-testid={ADMIN.brandUploadInput} type="file" accept="image/*" onChange={onPickFile} className="hidden" />
              <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] text-[var(--pg-admin-accent-fg)] font-semibold px-4 py-2 rounded-xl transition-colors">
                <Upload className="w-4 h-4" /> Choose file
              </button>
              <button type="button" onClick={reset} className="inline-flex items-center gap-2 border border-white/10 hover:bg-white/5 text-sm px-3 py-2 rounded-xl"><RefreshCcw className="w-4 h-4" /> Reset to default</button>
            </div>
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Logo size">
              <select data-testid={ADMIN.brandSizeSelect} value={brand.logo_size} onChange={(e) => setBrand({ ...brand, logo_size: e.target.value })} className="pg-inp">
                {SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Corner rounding">
              <select data-testid={ADMIN.brandRadiusSelect} value={brand.logo_radius} onChange={(e) => setBrand({ ...brand, logo_radius: e.target.value })} className="pg-inp">
                {RADIUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>

          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={brand.show_wordmark} onChange={(e) => setBrand({ ...brand, show_wordmark: e.target.checked })} className="w-4 h-4 accent-[var(--pg-admin-accent)]" />
            <span className="text-sm">Show “PADEL GEMS” text next to the logo</span>
          </label>

          <div className="pt-2">
            <button data-testid={ADMIN.brandSaveBtn} disabled={saving} onClick={save} className="inline-flex items-center gap-2 bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] disabled:opacity-60 text-[var(--pg-admin-accent-fg)] font-semibold px-4 py-2 rounded-xl"><Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save brand'}</button>
          </div>
        </div>

        <style>{`.pg-inp{width:100%;border:1px solid rgba(255,255,255,.1);background:rgba(0,0,0,.3);border-radius:.75rem;padding:.6rem .9rem;color:#fff;outline:none;transition:border-color .15s;}.pg-inp:focus{border-color:var(--pg-admin-accent);box-shadow:0 0 0 3px var(--pg-admin-accent-ring);}`}</style>
      </div>

      <div className="lg:col-span-5 rounded-2xl border border-white/10 bg-[#141414] p-6">
        <h2 className="font-display text-2xl">Preview</h2>
        <p className="mt-1 text-sm text-white/60">This is how your logo will appear across the site.</p>

        <div className="mt-6 space-y-4">
          <PreviewBox label="On the light navbar" bg="bg-[#FBF8EE]">
            <Logo brand={brand} />
          </PreviewBox>
          <PreviewBox label="On the dark admin sidebar" bg="bg-[#0B0C0F]" dark>
            <Logo brand={brand} invert showWordmark />
          </PreviewBox>
          <PreviewBox label="On the mustard brand bar" bg="bg-[#F2B705]">
            <Logo brand={brand} />
          </PreviewBox>
        </div>
      </div>

      <ConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Reset to the default logo?"
        description="The custom logo you uploaded will be replaced with the built-in Padel Gems mark. You can upload a new one anytime."
        confirmText="Reset logo"
        onConfirm={performReset}
      />
    </div>
  );
};

const PreviewBox = ({ label, bg, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest text-white/50 mb-1.5">{label}</div>
    <div className={`rounded-xl border border-white/10 ${bg} p-4 flex items-center`}>{children}</div>
  </div>
);

const SeoTab = () => {
  const empty = { meta_title: '', meta_description: '', meta_keywords: '', og_title: '', og_description: '', og_image: '', canonical_url: '', twitter_handle: '', robots: 'index, follow' };
  const [seo, setSeo] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/content');
      setSeo({ ...empty, ...(data?.seo?.extra || {}) });
    } catch (err) { logger.warn("Silent failure:", err); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put('/admin/seo', seo); toast.success('SEO settings saved. Reload the site to apply.'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 rounded-2xl border border-white/10 bg-[#141414] p-6">
        <h2 className="font-display text-2xl inline-flex items-center gap-2"><Search className="w-5 h-5 text-[var(--pg-admin-accent)]" /> SEO Metadata</h2>
        <p className="mt-1 text-sm text-white/60">Improve discoverability across search engines and social platforms.</p>

        <div className="mt-6 space-y-4">
          <Field label="Meta title (≤ 60 chars recommended)">
            <input data-testid={ADMIN.seoTitleInput} value={seo.meta_title} onChange={(e) => setSeo({ ...seo, meta_title: e.target.value })} className="pg-inp" />
          </Field>
          <Field label="Meta description (≤ 160 chars recommended)">
            <textarea data-testid={ADMIN.seoDescInput} rows={2} value={seo.meta_description} onChange={(e) => setSeo({ ...seo, meta_description: e.target.value })} className="pg-inp resize-none" />
          </Field>
          <Field label="Keywords (comma-separated)">
            <input data-testid={ADMIN.seoKeywordsInput} value={seo.meta_keywords} onChange={(e) => setSeo({ ...seo, meta_keywords: e.target.value })} className="pg-inp" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Open Graph title">
              <input value={seo.og_title} onChange={(e) => setSeo({ ...seo, og_title: e.target.value })} className="pg-inp" />
            </Field>
            <div>
              <ImagePicker
                label="Open Graph image (1200×630 recommended)"
                section="seo"
                aspect="1200/630"
                value={seo.og_image || '/padel-gems-og.png'}
                onChange={(url) => setSeo({ ...seo, og_image: url })}
                testId="seo-og-image-picker"
              />
              <p className="mt-1 text-[10px] text-white/40">Shown in link previews on WhatsApp, Instagram, Twitter, Google & more.</p>
            </div>
          </div>
          <Field label="Open Graph description">
            <textarea rows={2} value={seo.og_description} onChange={(e) => setSeo({ ...seo, og_description: e.target.value })} className="pg-inp resize-none" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Canonical URL"><input value={seo.canonical_url} onChange={(e) => setSeo({ ...seo, canonical_url: e.target.value })} className="pg-inp" placeholder="https://padelgems.com" /></Field>
            <Field label="Twitter handle"><input value={seo.twitter_handle} onChange={(e) => setSeo({ ...seo, twitter_handle: e.target.value })} className="pg-inp" placeholder="@hussain_alghafly" /></Field>
          </div>
          <Field label="Robots directive"><input value={seo.robots} onChange={(e) => setSeo({ ...seo, robots: e.target.value })} className="pg-inp" placeholder="index, follow" /></Field>

          <div className="pt-2">
            <button data-testid={ADMIN.seoSaveBtn} disabled={saving} onClick={save} className="inline-flex items-center gap-2 bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] disabled:opacity-60 text-[var(--pg-admin-accent-fg)] font-semibold px-4 py-2 rounded-xl"><Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save SEO'}</button>
          </div>
        </div>

        <style>{`.pg-inp{width:100%;border:1px solid rgba(255,255,255,.1);background:rgba(0,0,0,.3);border-radius:.75rem;padding:.6rem .9rem;color:#fff;outline:none;transition:border-color .15s;}.pg-inp:focus{border-color:var(--pg-admin-accent);box-shadow:0 0 0 3px var(--pg-admin-accent-ring);}`}</style>
      </div>

      <div className="lg:col-span-4 rounded-2xl border border-white/10 bg-[#141414] p-6">
        <div className="text-[11px] uppercase tracking-widest text-white/50">Google Preview</div>
        <div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-4">
          <div className="text-xs text-blue-300 truncate">{seo.canonical_url || 'https://your-domain.com'}</div>
          <div className="text-blue-400 text-lg leading-snug mt-1">{seo.meta_title || 'Meta title preview'}</div>
          <div className="text-white/70 text-sm mt-1 line-clamp-3">{seo.meta_description || 'Meta description preview will show here.'}</div>
        </div>

        <div className="mt-6 text-[11px] uppercase tracking-widest text-white/50">Social Preview</div>
        <div className="mt-3 rounded-xl border border-white/10 bg-black/30 overflow-hidden">
          {seo.og_image ? (
            <div className="aspect-[1.9/1] bg-white/5 flex items-center justify-center">
              <img src={seo.og_image} alt="OG" className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <div className="aspect-[1.9/1] bg-white/5 flex items-center justify-center text-white/40 text-xs gap-2"><ImageIcon className="w-4 h-4" /> No OG image</div>
          )}
          <div className="p-3">
            <div className="text-white font-semibold text-sm line-clamp-1">{seo.og_title || seo.meta_title || 'OG Title'}</div>
            <div className="text-white/60 text-xs line-clamp-2 mt-1">{seo.og_description || seo.meta_description || 'OG description preview.'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PasswordTab = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { toast.error('New passwords do not match.'); return; }
    setSaving(true);
    try {
      await api.post('/admin/change-password', { current_password: current, new_password: next });
      toast.success('Password changed successfully.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to change password'); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-lg rounded-2xl border border-white/10 bg-[#141414] p-6">
      <h2 className="font-display text-2xl inline-flex items-center gap-2"><KeyRound className="w-5 h-5 text-[var(--pg-admin-accent)]" /> Change Password</h2>
      <p className="mt-1 text-sm text-white/60">Use a strong, unique password. Minimum 8 characters.</p>
      <div className="mt-6 space-y-4">
        <Field label="Current password">
          <input data-testid={ADMIN.passwordCurrent} type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" className="pg-inp" />
        </Field>
        <Field label="New password">
          <input data-testid={ADMIN.passwordNew} type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" className="pg-inp" />
        </Field>
        <Field label="Confirm new password">
          <input data-testid={ADMIN.passwordConfirm} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" className="pg-inp" />
        </Field>
        <div className="pt-2">
          <button data-testid={ADMIN.passwordSaveBtn} disabled={saving} type="submit" className="inline-flex items-center gap-2 bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] disabled:opacity-60 text-[var(--pg-admin-accent-fg)] font-semibold px-4 py-2 rounded-xl"><Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Update password'}</button>
        </div>
      </div>
      <style>{`.pg-inp{width:100%;border:1px solid rgba(255,255,255,.1);background:rgba(0,0,0,.3);border-radius:.75rem;padding:.6rem .9rem;color:#fff;outline:none;transition:border-color .15s;}.pg-inp:focus{border-color:var(--pg-admin-accent);box-shadow:0 0 0 3px var(--pg-admin-accent-ring);}`}</style>
    </form>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
